import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { DbPortalIch } from '@gql/shared-types';
import { api, ApiFehler } from '../api/client.js';
import { useAuth } from '../auth/AuthKontext.js';
import { gemerktesToken, tokenAusAdresseUebernehmen, tokenVergessen } from '../auth/linkToken.js';

const TOKEN_MUSTER = /^[A-Za-z0-9_-]{43}$/;

/**
 * Löst den Link aus der E-Mail ein. Das Token steht im Fragment (#…) und erreicht daher keine Server-Protokolle;
 * main.tsx nimmt es vor dem ersten Rendern aus Adresszeile und Verlauf (auth/linkToken.ts).
 * Eingelöst wird erst nach Klick auf „Jetzt anmelden“: Mailfilter großer Unternehmen öffnen Links vorab
 * (auch mit JavaScript) und würden den Einmallink sonst verbrauchen, bevor die Person ihn anklickt.
 */
export function AnmeldenLink() {
  const { neuLaden } = useAuth();
  const navigate = useNavigate();
  const [token, setToken] = useState(gemerktesToken);
  const [fehler, setFehler] = useState<string | null>(null);
  // endgültig: Link ungültig, abgelaufen oder verbraucht – ein erneuter Versuch hilft nicht
  const [endgueltig, setEndgueltig] = useState(false);
  const [sendet, setSendet] = useState(false);
  const laeuft = useRef(false);
  const tokenGueltig = TOKEN_MUSTER.test(token);

  // Neuer Link im selben Tab: nur das Fragment ändert sich, die Seite lädt nicht neu
  useEffect(() => {
    const neuerLink = () => {
      tokenAusAdresseUebernehmen();
      laeuft.current = false;
      setSendet(false);
      setFehler(null);
      setEndgueltig(false);
      setToken(gemerktesToken());
    };
    window.addEventListener('hashchange', neuerLink);
    return () => window.removeEventListener('hashchange', neuerLink);
  }, []);

  const anmelden = () => {
    if (laeuft.current || !tokenGueltig) return;
    laeuft.current = true;
    setSendet(true);
    setFehler(null);
    api<DbPortalIch>('/anmeldung/einloesen', { methode: 'POST', json: { token } })
      .then(async (ich) => {
        tokenVergessen();
        await neuLaden();
        navigate(ich.nutzer?.ndaErforderlich ? '/vertraulichkeit' : '/standorte', { replace: true });
      })
      .catch((f: Error) => {
        // Netzwerkfehler, Drosselung, Serverfehler: Token bleibt gültig, erneuter Versuch möglich
        const aus = f instanceof ApiFehler && f.code === 'link_ungueltig';
        if (aus) tokenVergessen();
        setEndgueltig(aus);
        setFehler(f.message);
        setSendet(false);
        laeuft.current = false;
      });
  };

  return (
    <div className="seite seite-schmal">
      <h1>Anmeldung</h1>
      {!tokenGueltig || endgueltig ? (
        <div className="zustand-fehler hinweis-kasten" role="alert">
          <p>
            {endgueltig
              ? fehler
              : 'Bitte öffnen Sie den Link aus der E-Mail erneut. Ein noch nicht verwendeter Link bleibt bis zu seinem Ablauf gültig.'}
          </p>
          <p>
            <Link to="/anmelden">Neuen Anmeldelink anfordern</Link>
          </p>
        </div>
      ) : (
        <>
          <p>Bitte bestätigen Sie die Anmeldung. Der Link ist nur einmal verwendbar.</p>
          {fehler && (
            <div className="zustand-fehler hinweis-kasten" role="alert">
              <p>{fehler}</p>
            </div>
          )}
          <button type="button" className="knopf knopf-breit" onClick={anmelden} aria-disabled={sendet}>
            {fehler ? 'Erneut versuchen' : 'Jetzt anmelden'}
          </button>
          <p role="status">
            {sendet && (
              <>
                <span className="spinner" aria-hidden="true" /> Sie werden angemeldet …
              </>
            )}
          </p>
        </>
      )}
    </div>
  );
}
